import { z } from "zod"
import { PROMO_DISCOUNT_PERCENTS } from "./catalog-prices"
import { MEXICO_STATES } from "./mexico-states"

/** Admin login. */
export const loginSchema = z.object({
	email: z.string().email({ message: "Ingresa un email válido" }),
	password: z.string().min(1, { message: "Ingresa tu contraseña" })
})

export type LoginInput = z.infer<typeof loginSchema>

/** Máximo de caracteres del campo de comentarios del formulario de contacto. */
export const CONTACT_MAX_COMMENTS = 500

const NAME_CHARS = /^[\p{L}\s]+$/u

/** Formulario de contacto (tratamiento + página /contacto). */
export const contactSchema = z.object({
	name: z
		.string()
		.trim()
		.regex(NAME_CHARS, "Ingresa un nombre válido, sin números ni símbolos.")
		.refine(value => value.split(/\s+/).filter(word => word.length > 2).length >= 2, "Escribe tu nombre y apellido."),
	phone: z
		.string()
		.trim()
		.refine(value => /^(\+?52)?\d{10}$/.test(value.replace(/[\s-]/g, "")), "Por favor ingresa un número de México."),
	comments: z
		.string()
		.trim()
		.min(20, "Asegúrate de incluir los síntomas de tu padecimiento.")
		.max(CONTACT_MAX_COMMENTS, `Máximo ${CONTACT_MAX_COMMENTS} caracteres.`),
	treatment: z.string().trim().min(1, "Falta el identificador del tratamiento.")
})

export type ContactInput = z.infer<typeof contactSchema>

/** A single line item sent to Stripe Checkout. Solo acepta `price` IDs del catálogo. */
export const checkoutLineItemSchema = z
	.object({
		quantity: z.number().int().positive(),
		price: z.string().startsWith("price_")
	})
	.strict()

export type CheckoutLineItem = z.infer<typeof checkoutLineItemSchema>

export const checkoutShippingOptionSchema = z.union([
	z.object({ shipping_rate: z.string() }),
	z.object({
		shipping_rate_data: z.object({
			type: z.literal("fixed_amount"),
			fixed_amount: z.object({ amount: z.number().int(), currency: z.string() }),
			display_name: z.string(),
			delivery_estimate: z.object({
				minimum: z.object({ unit: z.string(), value: z.number().int() }),
				maximum: z.object({ unit: z.string(), value: z.number().int() })
			})
		})
	})
])

export type CheckoutShippingOption = z.infer<typeof checkoutShippingOptionSchema>

/** Body for POST /checkout-sessions (siempre ligado a una orden del paso 2). */
export const createCheckoutSessionSchema = z.object({
	orderId: z.string().uuid(),
	/**
	 * Origen del frontend que llama (`VITE_ORIGIN`, ya incluye la subruta `/previews/<rama>/`
	 * en preview). El Lambda de preview es compartido entre ramas, así que su `WEB_ORIGIN`
	 * estático no puede saber esa subruta; el API valida el host antes de usarlo.
	 */
	webOrigin: z.string().url().optional()
})

export type CreateCheckoutSessionInput = z.infer<typeof createCheckoutSessionSchema>

/** stripe = tarjetas/wallets; oxxo = efectivo vía Stripe. */
export const PAYMENT_PROVIDERS = ["stripe", "paypal", "oxxo", "bank_transfer"] as const

export const SHIPPING_METHOD_IDS = ["ordinario", "gratuito"] as const
export type ShippingMethodId = (typeof SHIPPING_METHOD_IDS)[number]

const mxPhoneSchema = z
	.string()
	.trim()
	.refine(value => /^(\+?52)?\d{10}$/.test(value.replace(/[\s-]/g, "")), "Por favor ingresa un número de México.")

const checkoutNameSchema = z
	.string()
	.trim()
	.regex(NAME_CHARS, "Ingresa un nombre válido, sin números ni símbolos.")
	.refine(value => value.split(/\s+/).filter(word => word.length > 2).length >= 2, "Escribe tu nombre y apellido.")

/** Dirección de envío MX (checkout paso 2). */
export const checkoutAddressSchema = z.object({
	line1: z.string().trim().min(3, "Ingresa la calle y número."),
	line2: z.string().trim().min(1, "Ingresa la colonia / manzana / delegación.").max(120),
	city: z.string().trim().min(2, "Ingresa la ciudad."),
	state: z.enum(MEXICO_STATES, {
		errorMap: () => ({ message: "Selecciona un estado." })
	}),
	zip: z
		.string()
		.trim()
		.regex(/^\d{5}$/, "Ingresa un código postal de 5 dígitos."),
	country: z.literal("MX").default("MX")
})

export type CheckoutAddressInput = z.infer<typeof checkoutAddressSchema>

/**
 * Forma "en bruto" de la dirección: acepta campos vacíos. La usan el formulario y el
 * body de creación de orden para poder mandar (u omitir) los 6 campos cuando se
 * reutiliza una dirección guardada por `addressId` — react-hook-form no desregistra
 * los inputs solo por ocultarlos, así que si esto exigiera `checkoutAddressSchema`
 * completo (line1/line2/etc. no vacíos) igual bloquearía el submit en silencio aunque
 * el picker ya tenga una tarjeta seleccionada. La validación fuerte de abajo solo se
 * exige cuando NO hay `addressId` (dirección nueva tecleada a mano).
 */
const rawAddressSchema = z.object({
	line1: z.string().trim().default(""),
	line2: z.string().trim().max(120).default(""),
	city: z.string().trim().default(""),
	state: z.string().trim().default(""),
	zip: z.string().trim().default(""),
	country: z.literal("MX").default("MX")
})

function checkAddressUnlessReused(
	data: { address: z.infer<typeof rawAddressSchema>; addressId?: string },
	ctx: z.RefinementCtx
): void {
	if (data.addressId) return
	const parsed = checkoutAddressSchema.safeParse(data.address)
	if (!parsed.success) {
		for (const issue of parsed.error.issues) {
			ctx.addIssue({ ...issue, path: ["address", ...issue.path] })
		}
	}
}

/** Formulario completo del paso 2 (sin email; el email va aparte / bloqueado). */
export const checkoutCustomerFormSchema = z
	.object({
		name: checkoutNameSchema,
		phone: mxPhoneSchema,
		address: rawAddressSchema,
		/** Tarjeta de dirección guardada elegida en el picker; ver `checkAddressUnlessReused`. */
		addressId: z.string().uuid().optional(),
		shippingMethod: z.enum(SHIPPING_METHOD_IDS),
		paymentProvider: z.enum(PAYMENT_PROVIDERS)
	})
	.superRefine(checkAddressUnlessReused)

export type CheckoutCustomerFormInput = z.infer<typeof checkoutCustomerFormSchema>

export const checkoutEmailLookupSchema = z.object({
	email: z.string().trim().email({ message: "Ingresa un email válido" })
})

export type CheckoutEmailLookupInput = z.infer<typeof checkoutEmailLookupSchema>

export const checkoutVerifyOtpSchema = z.object({
	email: z.string().trim().email({ message: "Ingresa un email válido" }),
	code: z
		.string()
		.trim()
		.regex(/^\d{6}$/, "Ingresa el código de 6 dígitos.")
})

export type CheckoutVerifyOtpInput = z.infer<typeof checkoutVerifyOtpSchema>

/** Body for POST /checkout/orders (paso 2 → Pendiente). */
export const createCheckoutOrderSchema = z
	.object({
		email: z.string().trim().email({ message: "Ingresa un email válido" }),
		name: checkoutNameSchema,
		phone: mxPhoneSchema,
		address: rawAddressSchema,
		/**
		 * Id de una dirección ya guardada del cliente (tarjeta elegida en el picker).
		 * Si viene y es válida (pertenece al cliente + sesión OTP verificada), se
		 * reutiliza esa fila tal cual en vez de crear una nueva a partir de `address`
		 * (que en ese caso puede venir vacío — ver `checkAddressUnlessReused`).
		 */
		addressId: z.string().uuid().optional(),
		shippingMethod: z.enum(SHIPPING_METHOD_IDS),
		/**
		 * Estimado de envío en centavos, solo para UI optimista mientras se crea la orden.
		 * El servidor recalcula el envío real a partir del peso del carrito y las promociones
		 * activas (ver `createCheckoutOrder`) — este valor se ignora para el cobro.
		 */
		deliveryCents: z.number().int().min(0).optional(),
		paymentProvider: z.enum(PAYMENT_PROVIDERS),
		productsForStripe: z.array(checkoutLineItemSchema).min(1),
		urlParams: z.record(z.string(), z.string()).default({}),
		/**
		 * Estimado de descuento en centavos, solo para UI optimista. El servidor recalcula
		 * el descuento real por línea a partir de las promociones activas en la DB — este
		 * valor se ignora para el cobro.
		 */
		discountCents: z.number().int().min(0).optional()
	})
	.superRefine(checkAddressUnlessReused)

export type CreateCheckoutOrderInput = z.infer<typeof createCheckoutOrderSchema>

export const updatePurchaseStatusSchema = z.object({
	status: z.enum(["paid", "failed", "Pendiente"])
})

export type UpdatePurchaseStatusInput = z.infer<typeof updatePurchaseStatusSchema>

export const createPaypalOrderSchema = z.object({
	orderId: z.string().uuid(),
	/** Ver `createCheckoutSessionSchema.webOrigin`. */
	webOrigin: z.string().url().optional()
})

export type CreatePaypalOrderInput = z.infer<typeof createPaypalOrderSchema>

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Usa formato YYYY-MM-DD")

/** Query for GET /admin/purchases. */
export const purchasesQuerySchema = z.object({
	from: isoDate.optional(),
	to: isoDate.optional(),
	limit: z.coerce
		.number()
		.int()
		.refine((n): n is 10 | 30 | 50 | 100 => [10, 30, 50, 100].includes(n), {
			message: "limit debe ser 10, 30, 50 o 100"
		})
		.default(10),
	sortBy: z.enum(["date", "name", "city", "amount", "status", "paymentProcessor", "paymentMethod"]).default("date"),
	sortDir: z.enum(["asc", "desc"]).default("desc"),
	q: z.string().trim().max(100).optional()
})

export type PurchasesQueryInput = z.infer<typeof purchasesQuerySchema>

/** Query for GET /admin/customers. */
export const customersQuerySchema = z.object({
	q: z.string().trim().max(100).optional()
})

export type CustomersQueryInput = z.infer<typeof customersQuerySchema>

const listLimitSchema = z.coerce
	.number()
	.int()
	.refine((n): n is 10 | 30 | 50 | 100 => [10, 30, 50, 100].includes(n), {
		message: "limit debe ser 10, 30, 50 o 100"
	})
	.default(10)

/** Query for GET /admin/products (aggregated sales). */
export const productSalesQuerySchema = z.object({
	from: isoDate.optional(),
	to: isoDate.optional(),
	q: z.string().trim().max(100).optional()
})

export type ProductSalesQueryInput = z.infer<typeof productSalesQuerySchema>

/** Body for creating/updating a promotion. */
export const promotionInputSchema = z
	.object({
		name: z.string().trim().min(1, "Nombre requerido").max(30, "Máximo 30 caracteres"),
		message: z.string().trim().min(1, "Mensaje requerido").max(100, "Máximo 100 caracteres"),
		startDate: isoDate,
		endDate: isoDate,
		freeShipping: z.boolean(),
		discountPercent: z
			.number()
			.int()
			.refine(
				(n): n is (typeof PROMO_DISCOUNT_PERCENTS)[number] => (PROMO_DISCOUNT_PERCENTS as readonly number[]).includes(n),
				{
					message: "Descuento no válido"
				}
			),
		itemIds: z.array(z.string().min(1)).min(1, "Selecciona al menos un ítem"),
		/** Si true, cierra promociones conflictivas por ítems antes de crear/actualizar. */
		replaceConflicts: z.boolean().optional()
	})
	.refine(data => data.endDate >= data.startDate, {
		message: "La fecha final debe ser posterior o igual a la de inicio",
		path: ["endDate"]
	})
	.refine(data => data.freeShipping || data.discountPercent > 0, {
		message: "La promoción debe incluir descuento o envío gratuito",
		path: ["discountPercent"]
	})

export type PromotionInput = z.infer<typeof promotionInputSchema>

export const MESSAGE_STATUSES = ["Nuevo", "Contestado", "SinRespuesta"] as const

/** Query for GET /admin/messages. */
export const messagesQuerySchema = z.object({
	from: isoDate.optional(),
	to: isoDate.optional(),
	q: z.string().trim().max(100).optional(),
	limit: listLimitSchema,
	sortBy: z.enum(["createdAt", "name", "status", "treatment"]).default("createdAt"),
	sortDir: z.enum(["asc", "desc"]).default("desc")
})

export type MessagesQueryInput = z.infer<typeof messagesQuerySchema>

/** Body for PATCH /admin/messages/:id. */
export const updateMessageStatusSchema = z.object({
	status: z.enum(MESSAGE_STATUSES)
})

export type UpdateMessageStatusInput = z.infer<typeof updateMessageStatusSchema>
